import React, { useMemo, useState, useCallback } from 'react';
import { Grid, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Select from 'modules/views/shared/Select';
import Chips from 'modules/views/shared/Chips';
import { FieldWrapper, IField } from 'modules/views/pages/PersonalInformation/components/fieldWrapper';

import { IProfileInformationSchema } from '../schema';
import { useStyles } from '../styles';

interface IWorkerTradesSectionProps {
  roleFields: IField[];
  masterListTrades: Array<{ id: string; name: string }>;
  nonMasterListTrades: Array<{ id: string; name: string }>;
  values: IProfileInformationSchema;
  errors: Record<string, { message: string } | undefined>;
  setValue: (key: keyof IProfileInformationSchema, value: any) => void;
  isTradesRequired?: boolean;
}

const WorkerTradesSection: React.FC<IWorkerTradesSectionProps> = ({
  roleFields,
  masterListTrades,
  nonMasterListTrades,
  values,
  errors,
  setValue,
  isTradesRequired,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isOtherTradeSelected, setOtherTradeSelected] = useState(false);

  const allTradeChips = useMemo(
    () => masterListTrades.map(tr => ({ value: tr.id, label: tr.name })),
    [masterListTrades],
  );

  const selectedTradeChips = useMemo(
    () =>
      (values.trades || [])
        .map(id => {
          const trade = masterListTrades.find(tr => tr.id === id);
          return trade ? { value: id, label: trade.name } : null;
        })
        .filter(Boolean) as { value: string; label: string }[],
    [values.trades, masterListTrades],
  );

  // Display names for already-saved other trades (resolve IDs to names)
  const extraChips = useMemo(() => {
    return (values.otherTradesList || []).map(trade => {
      if (trade.isSaved) {
        const foundOption = nonMasterListTrades.find(option => option.id === trade.OtherText);
        return {
          label: foundOption ? foundOption.name : trade.OtherText,
          value: trade.OtherText,
        };
      }
      return {
        label: trade.OtherText,
        value: trade.OtherText,
      };
    });
  }, [values.otherTradesList, nonMasterListTrades]);

  const handleToggleOtherTrade = useCallback(() => {
    setOtherTradeSelected(prev => !prev);
  }, []);

  const handleTradesChange = useCallback(
    (selected: { value: string; label: string }[]) => {
      setValue(
        'trades',
        selected.map(c => c.value),
      );
    },
    [setValue],
  );

  const handleExtraChipRemove = useCallback(
    (chipToRemove: { label: string; value: string }) => {
      const updated = (values.otherTradesList || []).filter(trade => trade.OtherText !== chipToRemove.value);
      setValue('otherTradesList', updated);
    },
    [values.otherTradesList, setValue],
  );

  const handleOtherTradeAdd = useCallback(
    (selectedValue: any) => {
      if (!selectedValue) return;
      const newTrade = {
        isSaved: false,
        OtherText: selectedValue.isNewAdded ? selectedValue.value : selectedValue.value,
      };
      const existingTrades = Array.isArray(values.otherTradesList) ? values.otherTradesList : [];
      const alreadyExists = existingTrades.some(trade => trade.OtherText === newTrade.OtherText);
      if (!alreadyExists && newTrade.OtherText) {
        setValue('otherTradesList', [...existingTrades, newTrade]);
      }
    },
    [values.otherTradesList, setValue],
  );

  return (
    <>
      <p className={classes.sectionTitle} data-testid="profile-section-title-worker-trades">
        {t('trades.title')}
      </p>

      <Grid container spacing={3} data-testid="profile-section-worker-trades">
        {roleFields.map((field, index) => {
          if (field.id === 'laborUnionNumber' && !values.isAffiliatedToLaborUnion) return null;
          return (
            <Grid item xs={12} md={6} lg={4} key={field.id}>
              <FieldWrapper
                field={field}
                index={index}
                value={values[field.id]}
                setValue={value => setValue(field.id as keyof IProfileInformationSchema, value)}
                error={errors[field.id]?.message}
                options={field.options}
              />
            </Grid>
          );
        })}
      </Grid>

      <Divider className={classes.divider} />

      <p className={classes.sectionTitle}>
        {t('trades.selectedTrades')}
        {isTradesRequired && <span className={classes.requiredMark}>*</span>}
      </p>
      <Chips
        chips={allTradeChips}
        selectedChips={selectedTradeChips}
        extraChips={extraChips}
        error={errors.trades?.message}
        testId="trades-chips"
        onChange={handleTradesChange}
        onExtraChipRemove={handleExtraChipRemove}
      />

      <div
        className={classes.addTradeButton}
        data-testid="add-other-trade-button"
        onClick={handleToggleOtherTrade}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleOtherTrade();
          }
        }}
      >
        <span style={{ marginRight: '8px', fontSize: '16px' }}>{isOtherTradeSelected ? '-' : '+'}</span>
        {t('trades.addOtherTrade')}
      </div>

      {isOtherTradeSelected && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Select
              options={[]}
              testId="otherTradesList-select"
              onChange={handleOtherTradeAdd}
              selectedValue={null}
              inputLabel={t('trades.otherTrade')}
              addOption={true}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default WorkerTradesSection;
